import { MUITab } from "@abb/arcadia-ui-react"
import { StatusCodes } from "http-status-codes"
import { useCallback, useContext, useEffect, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { digitalManagerApi } from "../../../../services/api"
import {
  DiMaDetailsHeader,
  DiMaDetailsHeaderProps,
} from "../../../components/DiMaDetailsHeader"
import { DiMaProgress } from "../../../components/DiMaProgress"
import { UserContext } from "../../../context/UserContext"
import { ApplicationRoleApproverTypeEnum } from "../../../models/ApplicationRoleApprover"
import { RoleRequestGet } from "../../../models/RoleRequest"
import { RoleRequestApprovalStatusEnum } from "../../../models/RoleRequestApproval"
import { useHashTab } from "../../../../hooks/useHashTab"
import { RoleRequestApprovals } from "./approvals_tab/RoleRequestApprovals"
import { RoleRequestDetails } from "./details_tab/RoleRequestDetails"
import { RoleRequestHistory } from "./history_tab/RoleRequestHistory"
import { RoleRequestRequestor } from "./requestor_tab/RoleRequestRequestor"

const returnURL = "/role-requests"

export function RoleRequestContainer() {
  const navigate = useNavigate()
  const { roleRequestId } = useParams()
  const { user, isLoadingUser } = useContext(UserContext)
  const { activeTab, handleTabChange } = useHashTab("details")

  const [roleRequest, setRoleRequest] = useState<RoleRequestGet>()
  const [reloadEntity, setReloadEntity] = useState<boolean>(true)
  const [isLoadingRoleRequest, setIsLoadingRoleRequest] = useState<boolean>(true)

  useEffect(() => {
    if (roleRequestId && reloadEntity === true) {
      setIsLoadingRoleRequest(true)
      digitalManagerApi
        .get<RoleRequestGet>(`/roleRequests/${roleRequestId}`)
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            setRoleRequest(res.data)
            setReloadEntity(false)
          } else {
            navigate(returnURL)
          }
        })
        .catch(() => {
          navigate(returnURL)
        })
        .finally(() => {
          setIsLoadingRoleRequest(false)
        })
    }
  }, [navigate, reloadEntity, roleRequestId])

  const reloadRoleRequest = useCallback(() => setReloadEntity(true), [])

  if (!roleRequestId) {
    return <Navigate to={returnURL} />
  }

  if (!roleRequest || isLoadingRoleRequest) {
    return <DiMaProgress />
  }

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsHeaderProps: DiMaDetailsHeaderProps = {
    url: returnURL,
    title: roleRequest.application.name,
    label: "Status",
    subtitle: roleRequest.status,
    hideCopyToClipboard: true,
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      <DiMaDetailsHeader {...detailsHeaderProps} />
      <MUITab
        tabItems={[
          { id: "details", label: "Details" },
          { id: "requestor", label: "Requestor" },
          {
            id: "approvals",
            label: "Approvals",
            badgeSize: "size-16",
            status: "primary",
            statusLabel: roleRequest.approvals
              .filter(
                (a) =>
                  (a.status === RoleRequestApprovalStatusEnum.Open &&
                    (a.userApproverType === ApplicationRoleApproverTypeEnum.Approver ||
                      a.userApproverType ===
                        ApplicationRoleApproverTypeEnum.BackupApprover)) ||
                  (a.status === RoleRequestApprovalStatusEnum.Forwarded &&
                    a.userApproverType === ApplicationRoleApproverTypeEnum.Consulted)
              )
              .length.toString(),
          },
          { id: "history", label: "History" },
        ]}
        value={activeTab}
        handleChange={handleTabChange}
      />

      {activeTab === "details" && (
        <RoleRequestDetails roleRequest={roleRequest} setRoleRequest={setRoleRequest} />
      )}

      {activeTab === "requestor" && (
        <RoleRequestRequestor roleRequest={roleRequest} user={user} />
      )}

      {activeTab === "approvals" && (
        <RoleRequestApprovals
          roleRequest={roleRequest}
          reloadRoleRequest={reloadRoleRequest}
          isLoadingApprovals={reloadEntity}
        />
      )}

      {activeTab === "history" && <RoleRequestHistory roleRequest={roleRequest} />}
    </div>
  )
}
